import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTourDriver } from '@components';
import type { TourDriverStep } from '@components';

/** A TourDriverStep extended with an optional route to navigate to before showing it */
export type GuidedTourStep = TourDriverStep & {
  route?: string;
};

export type TourDefinition = {
  /** Unique ID — passed to startTour(id) to trigger this tour */
  id: string;
  steps: GuidedTourStep[];
};

/** Runtime popover overrides keyed by step index, merged on top of the tour definition */
export type TourStepOverride = {
  title?: string;
  description?: string;
};

export type GuidedTourContextValue = {
  /**
   * Start a registered tour by ID. Navigates to the first step's route if needed.
   * Pass `stepOverrides` to inject dynamic content (e.g. from an API response)
   * into each step's popover — indexed by global step position.
   */
  startTour: (tourId: string, stepOverrides?: TourStepOverride[]) => void;
  /** Stop the currently running tour and clear all pending state. */
  stopTour: () => void;
  /** ID of the currently active tour, or null. */
  activeTourId: string | null;
  /** Dynamically register a tour definition (e.g. from a page component). */
  registerTour: (tour: TourDefinition) => void;
};

const PENDING_KEY = 'guided_tour_pending';

type PendingState = {
  tourId: string;
  stepIndex: number;
  stepOverrides?: TourStepOverride[];
};

const GuidedTourContext = createContext<GuidedTourContextValue | null>(null);

type Props = {
  children: React.ReactNode;
  /** Optional static tour definitions to register on mount */
  tours?: TourDefinition[];
};

const GuidedTourProvider: React.FC<Props> = ({ children, tours: initialTours = [] }) => {
  const history = useHistory();
  const location = useLocation();
  const toursRef = useRef<Map<string, TourDefinition>>(new Map());
  const {
    portal,
    start: driverStart,
    stop: driverStop,
    moveNext: driverMoveNext,
    getActiveIndex: driverGetActiveIndex,
    isActive,
  } = useTourDriver();
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;
  const [activeTourId, setActiveTourId] = useState<string | null>(null);

  useEffect(() => {
    initialTours.forEach((t) => toursRef.current.set(t.id, t));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const registerTour = useCallback((tour: TourDefinition) => {
    toursRef.current.set(tour.id, tour);
  }, []);

  const clearPending = useCallback(() => {
    sessionStorage.removeItem(PENDING_KEY);
  }, []);

  const stopTour = useCallback(() => {
    driverStop();
    setActiveTourId(null);
    clearPending();
  }, [clearPending, driverStop]);

  /**
   * Launch the tour driver for the slice of `tour.steps` starting at `fromIndex`.
   *
   * Steps are grouped into the current page's "segment" — the run ends at the
   * first step that belongs to a different route. When the user clicks Next on
   * the last step of a segment, the provider saves progress + overrides to
   * sessionStorage and navigates. The useEffect below picks it back up.
   */
  const launchSegment = useCallback(
    (tour: TourDefinition, fromIndex: number, stepOverrides?: TourStepOverride[]) => {
      const { id, steps } = tour;

      // Determine where this page's segment ends and which route comes next
      let segmentEnd = steps.length;
      let nextRoute: string | null = null;
      for (let i = fromIndex + 1; i < steps.length; i++) {
        if (steps[i].route && steps[i].route !== location.pathname) {
          segmentEnd = i;
          nextRoute = steps[i].route!;
          break;
        }
      }

      // Build driver steps: strip `route`, merge API overrides
      const driverSteps: TourDriverStep[] = steps
        .slice(fromIndex, segmentEnd)
        .map(({ route: _r, ...step }, i) => {
          const globalIndex = fromIndex + i;
          const override = stepOverrides?.[globalIndex];
          return {
            ...step,
            popover: {
              ...step.popover,
              ...(override?.title !== undefined && { title: override.title }),
              ...(override?.description !== undefined && { description: override.description }),
            },
          };
        });

      const pending: PendingState = { tourId: id, stepIndex: fromIndex, stepOverrides };
      sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
      setActiveTourId(id);

      driverStart({
        showProgress: steps.length > 1,
        steps: driverSteps,
        onDestroyStarted: () => {
          setActiveTourId(null);
          clearPending();
        },
        onNextClick: () => {
          const idx = driverGetActiveIndex() ?? 0;
          if (idx >= driverSteps.length - 1 && nextRoute) {
            // Hand off to the next page: persist progress + overrides and navigate
            const next: PendingState = { tourId: id, stepIndex: segmentEnd, stepOverrides };
            sessionStorage.setItem(PENDING_KEY, JSON.stringify(next));
            driverStop();
            history.push(nextRoute);
          } else {
            driverMoveNext();
          }
        },
      });
    },
    [
      clearPending,
      history,
      location.pathname,
      driverStart,
      driverStop,
      driverMoveNext,
      driverGetActiveIndex,
    ],
  );

  const startTour = useCallback(
    (tourId: string, stepOverrides?: TourStepOverride[]) => {
      const tour = toursRef.current.get(tourId);
      if (!tour) {
        console.warn(`GuidedTourProvider: no tour registered with id "${tourId}"`);
        return;
      }

      driverStop();
      clearPending();

      const firstRoute = tour.steps[0]?.route;
      if (firstRoute && firstRoute !== location.pathname) {
        const pending: PendingState = { tourId, stepIndex: 0, stepOverrides };
        sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
        setActiveTourId(tourId);
        history.push(firstRoute);
        return;
      }

      launchSegment(tour, 0, stepOverrides);
    },
    [clearPending, history, launchSegment, location.pathname, driverStop],
  );

  // After every navigation, check sessionStorage for a pending tour that belongs on this route
  useEffect(() => {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw || isActiveRef.current) return;

    let pending: PendingState;
    try {
      pending = JSON.parse(raw);
    } catch {
      clearPending();
      return;
    }

    const tour = toursRef.current.get(pending.tourId);
    if (!tour) return;

    const expectedRoute = tour.steps[pending.stepIndex]?.route;
    if (expectedRoute && expectedRoute !== location.pathname) return;

    // Small delay so the target page's elements are in the DOM
    const timeout = setTimeout(() => {
      launchSegment(tour, pending.stepIndex, pending.stepOverrides);
    }, 300);

    return () => clearTimeout(timeout);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const value: GuidedTourContextValue = { startTour, stopTour, activeTourId, registerTour };

  return (
    <GuidedTourContext.Provider value={value}>
      {children}
      {portal}
    </GuidedTourContext.Provider>
  );
};

export const useGuidedTour = (): GuidedTourContextValue => {
  const ctx = useContext(GuidedTourContext);
  if (!ctx) throw new Error('useGuidedTour must be used inside GuidedTourProvider');
  return ctx;
};

export { GuidedTourContext };
export default GuidedTourProvider;
